import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ShopMap from '../../common/ShopMap/ShopMap';
import AvatarFrame from '../../common/AvatarFrame/AvatarFrame';
import { getProfilePhotoSrc, PLACEHOLDER_DATA_URI } from '../../../utils/profilePhoto';
import './Store.css';

/**
 * Public store page for a verified middleman shop - referenced from
 * the map picker, shipping flow, and the owner's profile. Separate
 * from the owner's personal /profile page.
 */
function Store() {
    const { shopId } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetch(`/api/shops/${shopId}`)
            .then(async r => {
                const json = await r.json();
                if (!r.ok) throw new Error(json.error || 'Store not found.');
                setData(json);
            })
            .catch(e => setError(e.message));
    }, [shopId]);

    if (error) return <div className="requests-container"><div className="requests-placeholder">{error}</div></div>;
    if (!data) return <div className="requests-container"><div className="requests-placeholder">Loading store&hellip;</div></div>;

    const { shop, ratings } = data;

    return (
        <div className="requests-container store-page">
            <header className="store-head">
                <div className="store-head-main">
                    <h1 className="store-name">{shop.name}</h1>
                    <div className="store-trust">
                        {shop.rating_avg
                            ? <><span className="store-stars">{'★'.repeat(Math.round(shop.rating_avg))}</span> {shop.rating_avg} · {shop.rating_count} review{shop.rating_count !== 1 ? 's' : ''}</>
                            : 'No reviews yet'}
                    </div>
                    <div className="store-meta">
                        Verified middleman · run by{' '}
                        <Link to={`/profile/${shop.user_id}`}>{shop.owner_username}</Link>
                        {' '}· on TradeSpot since {new Date(shop.created_at).toLocaleDateString()}
                    </div>
                </div>
            </header>

            <div className="store-columns">
                <section className="ship-panel">
                    <h3>About</h3>
                    {shop.description
                        ? <p className="store-desc">{shop.description}</p>
                        : <p className="ship-hint">No description yet.</p>}
                    {shop.fee_info && (
                        <>
                            <div className="ship-field-label">Middleman fees</div>
                            <p className="store-desc">{shop.fee_info}</p>
                        </>
                    )}
                    {shop.address && (
                        <>
                            <div className="ship-field-label">Location</div>
                            <p className="store-desc">{shop.address}</p>
                        </>
                    )}
                    {shop.lat != null && (
                        <div className="ship-map-wrap">
                            <ShopMap shops={[shop]} height={240} />
                        </div>
                    )}
                </section>

                <section className="ship-panel">
                    <h3>Reviews</h3>
                    {ratings.length === 0 ? (
                        <p className="ship-hint">
                            Reviews come from completed trades this store handled as middleman.
                        </p>
                    ) : (
                        <ul className="store-reviews">
                            {ratings.map((r, i) => (
                                <li key={i}>
                                    <div className="store-review-head">
                                        <Link
                                            to={`/profile/${r.user_id}`}
                                            className="store-review-user"
                                            title={`View ${r.username}'s profile`}
                                        >
                                            <AvatarFrame user={r}>
                                                <img
                                                    src={getProfilePhotoSrc(r)}
                                                    alt={r.username}
                                                    onError={e => { e.target.onerror = null; e.target.src = PLACEHOLDER_DATA_URI; }}
                                                />
                                            </AvatarFrame>
                                            <span className="store-review-who">{r.username}</span>
                                        </Link>
                                        <span className="store-stars">{'★'.repeat(r.rating)}<span className="store-stars-dim">{'★'.repeat(5 - r.rating)}</span></span>
                                        <span className="store-review-date">{new Date(r.created_at).toLocaleDateString()}</span>
                                    </div>
                                    {r.comment && <p className="store-review-comment">{r.comment}</p>}
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>
        </div>
    );
}

export default Store;
